'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaUser } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BlogPageDE() {
  const { t } = useLanguage();

  const blogPosts = [
    {
      id: 1,
      title: 'Botox Preise 2026 - Wie viel, wovon hängt es ab?',
      excerpt: 'Eine der meistgestellten Fragen von Menschen, die über Botox nachdenken: "Wie viel kosten Botox-Behandlungen 2026?" In diesem Artikel besprechen wir aktuelle Preise, Anwendungsprozess und Wirkung im Detail.',
      image: '/images/terleme-tedavisi.webp',
      date: '2026-01-15',
      readTime: '8 Minuten',
      author: 'Op. Dr. Barış Çin',
      slug: 'botox-preise-2026',
      category: 'Ästhetische Behandlungen'
    }
  ];

  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/de/blog';
  const pageTitle = 'Dr. Barış Çin Blog - Plastische Chirurgie und Schönheitstipps';
  const pageDescription = 'Aktuelle Informationen über plastische Chirurgie, ästhetische Operationen und Schönheit auf Dr. Barış Çin Blog-Seite.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Startseite', href: '/de' },
              { label: 'Blog', href: '/de/blog' },
            ]}
          />
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12 mt-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-center mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
            Dr. Barış Çin Blog
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Unsere Blog-Seite, auf der wir Expertenmeinungen zu ästhetischer Chirurgie, Schönheitsbehandlungen und Gesundheitsthemen teilen.
          </p>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <button className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors">
            Alle Artikel
          </button>
          <button className="px-6 py-2 bg-white text-gray-600 rounded-full hover:bg-gray-50 transition-colors border">
            Ästhetische Behandlungen
          </button>
          <button className="px-6 py-2 bg-white text-gray-600 rounded-full hover:bg-gray-50 transition-colors border">
            Chirurgische Eingriffe
          </button>
          <button className="px-6 py-2 bg-white text-gray-600 rounded-full hover:bg-gray-50 transition-colors border">
            Pflegetipps
          </button>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <Link
              key={post.id}
              href={`/de/blog/${post.slug}`}
              className="group"
            >
              <article className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2">
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <Image
                    src={post.image}
                    alt={post.title}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {post.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                    {post.title}
                  </h2>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Meta Information */}
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <FaUser className="w-4 h-4 mr-1" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center">
                        <FaClock className="w-4 h-4 mr-1" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <FaCalendarAlt className="w-4 h-4 mr-1" />
                      <span>{new Date(post.date).toLocaleDateString('de-DE')}</span>
                    </div>
                  </div>
                </div>
              </article>
            </Link>
          ))}
        </div>

        {/* Empty State for More Posts */}
        <div className="text-center mt-12">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Weitere Inhalte kommen bald!
            </h3>
            <p className="text-gray-600 mb-6">
              Folgen Sie uns für weitere informative Inhalte über ästhetische Chirurgie und Schönheitsbehandlungen.
            </p>
            <Link
              href="/de"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
            >
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
} 